
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner'; // 🎯 Используем sonner — он работает в виджете!
import { useQueryClient } from '@tanstack/react-query';
import { useRealtime } from '@/contexts/RealtimeContext';
import { getActiveOnlineWidgetConfig } from '@/lib/onlineWidgetClient';

interface BookingData {
  room_type: string;
  room_id?: string;
  // 🆕 Для виджетных бронирований: выбранная категория (для авто-назначения номера)
  category_id?: string;
  check_in_date: string;
  check_out_date: string;
  check_in_time?: string;
  check_out_time?: string;
  number_of_guests: number;
  adult_count?: number;
  children_count?: number;
  infants_count?: number;
  // 🎯 ДОБАВЛЕНО: Поля из виджета бронирования
  extra_beds?: number;
  cribs_count?: number;
  children_ages?: number[]; // Массив возрастов детей
  dietary_restrictions?: string;
  special_requests?: string;
  total_amount: number;
  hotel_id: string;
  rate_plan_id?: string;
  source?: string; // 🎯 Источник бронирования: widget, manual, direct, etc.
  email_required?: boolean;
  ignore_availability?: boolean; // 🌰 Орешник: бэк не назначает конкретный room_id
}

interface GuestInfo {
  guest_name: string;
  // 🎯 Явные поля ФИО (НЕ парсим из guest_name)
  first_name: string;
  last_name: string;
  middle_name?: string;
  guest_email?: string | null;
  guest_phone: string;
}

export const usePublicBooking = () => {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { broadcastBookingChange } = useRealtime();

  const createBooking = async (bookingData: BookingData & GuestInfo) => {
    setLoading(true);
    console.log('📋 Creating booking via Edge Function:', {
      hotel_id: bookingData.hotel_id,
      room_type: bookingData.room_type,
      total_amount: bookingData.total_amount,
      guest_name: bookingData.guest_name,
      guest_email: bookingData.guest_email
    });
    
    try {
      // Валидация входных данных
      if (!bookingData.hotel_id) {
        console.error('❌ hotel_id отсутствует');
        throw new Error('hotel_id не указан');
      }

      if (!bookingData.total_amount || bookingData.total_amount <= 0) {
        console.error('❌ Некорректная стоимость:', bookingData.total_amount);
        throw new Error('Некорректная стоимость бронирования');
      }

      if (!bookingData.guest_phone) {
        console.error('❌ Отсутствует телефон гостя');
        throw new Error('Заполните телефон гостя');
      }

      // Подготавливаем данные для Edge Function
      // 🎯 ВАЖНО: first_name/last_name приходят явно из формы (152‑ФЗ / Selectel)
      const rawEmail = (bookingData.guest_email || '').trim();

      const payload = {
        hotel_id: bookingData.hotel_id,
        room_type: bookingData.room_type,
        room_id: bookingData.room_id,
        category_id: bookingData.category_id,
        check_in_date: bookingData.check_in_date,
        check_out_date: bookingData.check_out_date,
        number_of_guests: bookingData.number_of_guests,
        special_requests: bookingData.special_requests,
        total_amount: bookingData.total_amount,
        guest: {
          email: rawEmail,
          firstname: bookingData.first_name?.trim() || 'Гость',
          lastname: bookingData.last_name?.trim() || '',
          phone: bookingData.guest_phone
        }
      };

      console.log('🚀 Calling room-grid-lite Edge Function with payload:', payload);

      // First create guest in Selectel for data protection compliance
      const unifiedGuestId = `GUEST_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // 🎯 Имя/фамилия всегда берём из явных полей формы
      const firstName = payload.guest.firstname;
      const lastName = payload.guest.lastname;
      // 🎯 full_name в русском порядке: Фамилия Имя Отчество
      const fullName = `${lastName} ${firstName} ${bookingData.middle_name || ''}`.trim();
      
      const selectelGuestData = {
        guest_id: unifiedGuestId,
        hotel_id: bookingData.hotel_id,
        full_name: fullName,
        first_name: firstName,
        last_name: lastName,
        middle_name: bookingData.middle_name || null,
        email: payload.guest.email || null,
        phone: payload.guest.phone,
        ip_address: '127.0.0.1',
        user_agent: 'QuickBookingModal',
        consent_text: 'Согласие на обработку персональных данных при бронировании'
      };

      // 🛡️ AK-47 Слой 2: строгая валидация контактов перед Selectel
      // Бронь без валидных контактов = мусор (152-ФЗ + операционно)
      const phoneStr = (payload.guest.phone || '').trim();
      const emailStr = (payload.guest.email || '').trim();
      const nameStr = (payload.guest.firstname || '').trim();
      const phoneDigits = phoneStr.replace(/\D/g, '');
      const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr);
      const emailRequired = bookingData.source === 'widget' && bookingData.email_required !== false;
      const hasEmail = emailStr.length > 0;

      if (!nameStr || nameStr === 'Гость') {
        throw new Error('Укажите имя гостя');
      }
      if (phoneDigits.length < 10) {
        throw new Error('Укажите корректный телефон гостя');
      }
      if (emailRequired && !emailValid) {
        throw new Error('Укажите корректный email гостя');
      }
      if (!emailRequired && hasEmail && !emailValid) {
        throw new Error('Укажите корректный email гостя');
      }

      const onlineConfig = getActiveOnlineWidgetConfig();
      const invokeWithTimeout = async (functionName: string, body: any, timeoutMs = 12000) => {
        const request = supabase.functions.invoke(functionName, { body });
        const timeout = new Promise<never>((_, reject) => {
          window.setTimeout(() => reject(new Error(`${functionName} timeout`)), timeoutMs);
        });
        return Promise.race([request, timeout]);
      };

      // 🛡️ AK-47 Атомарность: 1 ретрай при сбое сети, потом отказ брони
      let selectelData: any = null;
      let selectelError: any = null;
      for (let attempt = 1; attempt <= 2; attempt++) {
        const resp = await invokeWithTimeout('selectel-proxy', {
          operation: 'create_guest',
          data: selectelGuestData
        });
        selectelData = resp.data;
        selectelError = resp.error;
        if (!selectelError && selectelData) break;
        console.warn(`⚠️ Selectel create_guest attempt ${attempt} failed:`, selectelError);
        if (attempt < 2) await new Promise(r => setTimeout(r, 600));
      }

      if (selectelError || !selectelData) {
        // ❌ Атомарность: контакты НЕ сохранены в CRM → бронь не создаём
        console.error('❌ Selectel guest creation failed after retry — aborting booking');
        throw new Error('Не удалось сохранить контакты гостя. Проверьте подключение к интернету и попробуйте ещё раз.');
      }

      // Create booking using room-grid-lite (no category validation required)
      // 🎯 Передаём source для идентификации канала бронирования
      const { data: result, error: functionError } = await invokeWithTimeout('room-grid-lite', {
          operation: 'quick_booking',
          hotel_id: bookingData.hotel_id,
          room_id: bookingData.room_id,
          category_id: bookingData.category_id || null,
          check_in_date: bookingData.check_in_date,
          check_out_date: bookingData.check_out_date,
          check_in_time: bookingData.check_in_time,
          check_out_time: bookingData.check_out_time,
          number_of_guests: bookingData.number_of_guests,
          adult_count: bookingData.adult_count || bookingData.number_of_guests || 1,
          children_count: bookingData.children_count || 0,
          infants_count: bookingData.infants_count || 0,
          // 🎯 ДОБАВЛЕНО: Поля из виджета бронирования
          extra_beds: bookingData.extra_beds || 0,
          cribs_count: bookingData.cribs_count || 0,
          children_ages: bookingData.children_ages || null,
          dietary_restrictions: bookingData.dietary_restrictions || null,
          total_amount: bookingData.total_amount,
          rate_plan_id: bookingData.rate_plan_id || null,
          source: bookingData.source || 'manual', // 🎯 Источник: widget, manual, direct
          email_required: bookingData.email_required ?? false,
          ignore_availability: bookingData.ignore_availability === true, // 🌰 Орешник
          special_requests: bookingData.special_requests || null, // 💬 Комментарии
          guest_name: payload.guest.firstname + ' ' + payload.guest.lastname,
          guest_email: payload.guest.email,
          guest_phone: payload.guest.phone,
          guest_id: unifiedGuestId,
          selectel_guest_id: selectelData?.guest_id || unifiedGuestId,
          online_proxy_url: onlineConfig?.proxyUrl || null
      });

      if (functionError) {
        console.error('❌ Edge Function error:', functionError);
        
        // Извлекаем детальное сообщение об ошибке если доступно
        let errorMessage = functionError.message || 'Ошибка при создании бронирования';
        
        // Пытаемся получить более подробную информацию об ошибке
        if (functionError.context) {
          console.log('📝 Error context:', functionError.context);
        }
        
        throw new Error(errorMessage);
      }

      if (!result) {
        throw new Error('Нет ответа от сервера');
      }

      if (!result.success) {
        console.error('❌ Booking creation failed:', result);
        
        // Переводим распространенные ошибки на русский
        let errorMessage = result.error || 'Не удалось создать бронирование';
        
        if (errorMessage.includes('No available rooms')) {
          errorMessage = 'На выбранные даты нет свободных номеров этой категории. Попробуйте выбрать другие даты.';
        } else if (errorMessage.includes('Invalid dates')) {
          errorMessage = 'Некорректные даты бронирования';
        } else if (errorMessage.includes('guest')) {
          errorMessage = 'Ошибка в данных гостя';
        }
        
        throw new Error(errorMessage);
      }

      console.log('✅ Booking created successfully via room-grid-lite:', {
        booking_id: result.booking?.id,
        guest_id: unifiedGuestId
      });
      
      // Generate booking reference from booking ID
      const bookingReference = result.booking?.booking_reference || `BK-${result.booking?.id?.substring(0, 8).toUpperCase()}`;
      
      // 🎯 AK-47: Инвалидируем ВСЕ кэши бронирований для глобальной синхронизации
      await queryClient.invalidateQueries({ queryKey: ['optimized-bookings'] });
      await queryClient.invalidateQueries({ queryKey: ['bookings'] });
      
      // 📢 AK-47: Broadcast для ДРУГИХ компьютеров
      if (result.booking?.id) {
        broadcastBookingChange('INSERT', result.booking.id);
      }
      console.log('✅ Глобальные кэши бронирований инвалидированы + broadcast отправлен');
      
      // 🎯 Показываем БОЛЬШОЙ toast на 30 секунд с полной информацией (sonner)
      const checkInFormatted = new Date(bookingData.check_in_date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
      const checkOutFormatted = new Date(bookingData.check_out_date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
      const nights = Math.ceil((new Date(bookingData.check_out_date).getTime() - new Date(bookingData.check_in_date).getTime()) / (1000 * 60 * 60 * 24));
      
      toast.success(
        `🎉 Бронирование создано!\n\n` +
        `📋 Номер брони: ${bookingReference}\n` +
        `🏨 ${bookingData.room_type}\n` +
        `📅 ${checkInFormatted} — ${checkOutFormatted} (${nights} ${nights === 1 ? 'ночь' : nights < 5 ? 'ночи' : 'ночей'})\n` +
        `👥 Гостей: ${bookingData.number_of_guests}\n` +
        `💰 Сумма: ${bookingData.total_amount?.toLocaleString('ru-RU')} ₽\n\n` +
        `⏳ Ожидайте ответа администратора`,
        { duration: 30000 } // 30 секунд
      );

      return { 
        success: true, 
        booking: result.booking,
        guest: { guest_id: unifiedGuestId },
        booking_reference: bookingReference
      };

    } catch (error) {
      console.error('💥 Ошибка в процессе создания бронирования:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
      
      toast.error(`Ошибка: ${errorMessage}`);

      return { 
        success: false, 
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    createBooking,
    createPublicBooking: createBooking, // Alias for backward compatibility
    loading
  };
};
